/**
 * On-canvas gradient handles — the geometry behind Figma's gradient gizmo.
 *
 * The document stores a gradient the way CSS states one: a linear fill is an
 * angle (0° paints upwards, 90° to the right, clockwise), a radial fill is a
 * centre in percent of the box. These helpers turn that into the line and the
 * points the canvas draws, and back again while a handle is being dragged — all
 * in ELEMENT-LOCAL pixels, so a rotated element's gizmo rotates with it exactly
 * as its paint does.
 */

const DEG = Math.PI / 180;

/** The fill an element paints with: shapes keep it in `fillType`, the rest in `backgroundType`. */
export function mainFillType(styles = {}) {
    return styles.backgroundType || styles.fillType || 'solid';
}

export const isGradientFill = (styles = {}) => {
    const t = mainFillType(styles);
    return t === 'linear' || t === 'radial';
};

/** CSS's own gradient-line length for a box: |w·sinθ| + |h·cosθ|. */
export function gradientLineLength(width, height, angleDeg) {
    const a = angleDeg * DEG;
    return Math.abs(width * Math.sin(a)) + Math.abs(height * Math.cos(a));
}

/**
 * The two ends of the gradient line in element-local pixels. `start` is where
 * the first colour sits, `end` where the second one does.
 */
export function gradientLinePoints(width, height, angleDeg) {
    const a = angleDeg * DEG;
    // CSS y grows downwards, so "up" (0°) is -y.
    const dx = Math.sin(a);
    const dy = -Math.cos(a);
    const half = gradientLineLength(width, height, angleDeg) / 2;
    const cx = width / 2;
    const cy = height / 2;
    return {
        center: { x: cx, y: cy },
        start: { x: cx - dx * half, y: cy - dy * half },
        end: { x: cx + dx * half, y: cy + dy * half },
    };
}

/** A scene point in the element's own unrotated frame, measured from its top-left. */
export function toLocalPoint(pt, box, rotationDeg = 0) {
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;
    const r = (rotationDeg || 0) * DEG;
    const cos = Math.cos(r);
    const sin = Math.sin(r);
    const sx = pt.x - cx;
    const sy = pt.y - cy;
    return {
        x: sx * cos + sy * sin + box.width / 2,
        y: -sx * sin + sy * cos + box.height / 2,
    };
}

/**
 * The angle a dragged endpoint asks for, 0–360. Dragging the START end points
 * the gradient the other way, so it is the opposite bearing.
 */
export function angleFromLocalPoint(local, width, height, which = 'end') {
    const dx = local.x - width / 2;
    const dy = local.y - height / 2;
    if (!dx && !dy) return null;
    let deg = Math.atan2(dx, -dy) / DEG;
    if (which === 'start') deg += 180;
    return ((deg % 360) + 360) % 360;
}

/** Radial centre in percent of the box, clamped to it. */
export function centerPercentFromLocal(local, width, height) {
    const pct = (v, size) => Math.round(Math.min(100, Math.max(0, (v / Math.max(1, size)) * 100)));
    return { x: pct(local.x, width), y: pct(local.y, height) };
}

/** Farthest-corner distance — the extent CSS gives `radial-gradient(circle at …)`. */
export function radialExtent(width, height, cxPct, cyPct) {
    const cx = (cxPct / 100) * width;
    const cy = (cyPct / 100) * height;
    return Math.max(
        Math.hypot(cx, cy),
        Math.hypot(width - cx, cy),
        Math.hypot(cx, height - cy),
        Math.hypot(width - cx, height - cy),
    );
}
